import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import useThrottle from 'src/hooks/useThrottle';
import { IFinancialPayment } from 'src/@types/Choqorak/MainTables/FinancialPayment/FinancialPayment';

// ----------------------------------------------------------------------

export default observer(function FinancialPaymentNewEditForm() {
  const { FinancialPaymentStore, ChoqorakCommonDropdown } = useStore();
  const { translate } = useLocales();
  const { createFinancialPayment, updateFinancialPayment, editMode, selectedFinancialPayment, clearSelectedFinancialPayment } = FinancialPaymentStore;
  const { loadCurrencyTypeDDL, CurrencyTypeOption } =
    ChoqorakCommonDropdown;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewFinancialPaymentSchema = Yup.object().shape({
    description: Yup.string().required(`${translate('Validation.description')}`),
    paymentDate: Yup.string().required(`${translate('Validation.paymentDate')}`),
  });

  const defaultValues = useMemo<IFinancialPayment>(
    () => ({
      financialPaymentId: selectedFinancialPayment?.financialPaymentId,
      dollor: selectedFinancialPayment?.dollor || undefined,
      afghani: selectedFinancialPayment?.afghani || undefined,
      description: selectedFinancialPayment?.description || '',
      serialNumber: selectedFinancialPayment?.serialNumber || '',
      paymentDate: selectedFinancialPayment?.paymentDate || new Date(),
      attachment: selectedFinancialPayment?.attachment || undefined,
    }),
    [selectedFinancialPayment]
  );

  const methods = useForm<IFinancialPayment>({
    resolver: yupResolver(NewFinancialPaymentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: IFinancialPayment) => {
    const newData = {
      ...data,
      branchId: 1,
      paymentDate: new Date(data.paymentDate!),
    };

    try {
      if (data.financialPaymentId === undefined) {
        // --- create ---
        await createFinancialPayment(newData);
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.FinancialPayment.list);
      } else {
        // --- update ---
        await updateFinancialPayment(newData);
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        navigate(PATH_DASHBOARD.FinancialPayment.list);
      }
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      } catch {
        setError('afterSubmit', { ...err, message: err.message || 'Unexpected error' });
      }
    }
  };


  useEffect(() => {
    loadCurrencyTypeDDL();
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >

             
              <RHFTextField name="dollor" label={translate('Report.Dollor')} showAsterisk={true} />
              <RHFTextField name="afghani" label={translate('Report.Afghani')} showAsterisk={true} />
              <LocalizDatePicker
                name="paymentDate"
                label={translate('FinancialPayment.Date')}
                control={control}
                showAsterisk={false}
              />
              <RHFTextField
                name="serialNumber"
                label={translate('RentPaymentPrint.SerialNumber')}
                showAsterisk={true}
              />
              <RHFTextField
                name="description"
                label={translate('FinancialPayment.Description')}
                showAsterisk={true}
              />

              <Stack spacing={2}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    name="attachment"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label="Attach File"
                    name="attachment"
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedFinancialPayment();
                  navigate(PATH_DASHBOARD.FinancialPayment.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
